import { PageNotice } from '../../../shared/components/PageFeedback';
import PartyActivityMetrics from '../../party-activity/PartyActivityMetrics';
import PartyActivityMetricDialog from '../../party-activity/PartyActivityMetricDialog';
import usePartyActivity from '../../party-activity/usePartyActivity';
import useRegisteredModuleFilters from '../../moduleFilters';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { buildRouteLocation, ROUTE_IDS } from '@/app/router/route-registry';
import ClientsHeader from '@/modules/clients/components/ClientsHeader';
import ClientsFilters from '@/modules/clients/components/ClientsFilters';
import ClientsTable from '@/modules/clients/components/ClientsTable';
import ClientDetailDrawer from '@/modules/clients/components/ClientDetailDrawer';
import CreateClientDialog from '@/modules/clients/components/CreateClientDialog';
import EditClientDialog from '@/modules/clients/components/EditClientDialog';
import { useClientsDialogs } from '@/modules/clients/hooks/useClientsDialogs';
import { useClientsData } from '@/modules/clients/hooks/useClientsData';
import { useClientsFilters } from '@/modules/clients/hooks/useClientsFilters';
import {
  ConfirmActionDialog,
  PageActionRow,
  PageDivider,
  PageFrame,
  PrimaryActionButton,
} from '../../../shared/components/patterns';
import { toast } from '../../../shared/hooks/use-toast';
import '@/modules/clients/styles/clients.css';

function ClientsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { filters, setSearch, setTipoCliente } = useClientsFilters();
  const {
    staff,
    staffUnavailable,
    staffError,
    staffCatalogStatus,
    clients,
    isLoading,
    clientsCatalogStatus,
    filteredClients,
    counters,
    emptyClient,
    createClient,
    updateClient,
    archiveClient,
    restoreClient,
    deleteClientPermanently,
    saveNotes,
    getFinancials,
    getFinancialsMeta,
    loadFinancials,
    getClientActivity,
    getClientActivityMeta,
    loadClientActivity,
    loadError,
  } = useClientsData(filters);
  const dialogs = useClientsDialogs();
  const moduleFilters = useRegisteredModuleFilters('clients', clients);
  const clientsModuleDisabled = clientsCatalogStatus === 'module_disabled';
  const clientsTenantDatabaseUnavailable = clientsCatalogStatus === 'tenant_database_unavailable';
  const activity = usePartyActivity('client', clients, {
    enabled: ['available', 'empty'].includes(clientsCatalogStatus), loading: isLoading,
    onMetricClick: () => { setSearch(''); setTipoCliente('tutti'); moduleFilters.onReset(); },
  });
  const [archiveRequest, setArchiveRequest] = useState(null);
  const [deleteRequest, setDeleteRequest] = useState(null);

  useEffect(() => {
    if (!dialogs.selectedClient?.id) return;
    loadFinancials(dialogs.selectedClient.id);
    loadClientActivity(dialogs.selectedClient.id);
  }, [dialogs.selectedClient?.id, loadFinancials, loadClientActivity]);

  useEffect(() => {
    const clientIdParam = searchParams.get('clientId');
    const searchQuery = searchParams.get('search');
    const action = searchParams.get('action');

    if (action === 'new') {
      dialogs.openCreate();
      setSearchParams({});
      return;
    }

    if (clientIdParam) {
      const normalizedClientId = Number(clientIdParam);
      const foundClient = clients.find((client) => client.id === normalizedClientId);

      if (foundClient) {
        dialogs.openDetail(foundClient);
        setSearchParams({});
        return;
      }
    }

    if (!searchQuery) return;

    setSearch(searchQuery);
    const foundClient = clients.find(
      (client) =>
        client.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        client.company.toLowerCase().includes(searchQuery.toLowerCase())
    );

    if (foundClient) dialogs.openDetail(foundClient);
    setSearchParams({});
  }, [searchParams, setSearch, clients, dialogs, setSearchParams]);

  const selectedFinancials = useMemo(
    () => (dialogs.selectedClient ? getFinancials(dialogs.selectedClient.id) : null),
    [dialogs.selectedClient, getFinancials]
  );
  const selectedFinancialsMeta = useMemo(
    () =>
      dialogs.selectedClient
        ? getFinancialsMeta(dialogs.selectedClient.id)
        : { isLoading: false, unavailable: false },
    [dialogs.selectedClient, getFinancialsMeta]
  );
  const selectedActivity = useMemo(
    () => (dialogs.selectedClient ? getClientActivity(dialogs.selectedClient.id) : null),
    [dialogs.selectedClient, getClientActivity]
  );
  const selectedActivityMeta = useMemo(
    () =>
      dialogs.selectedClient
        ? getClientActivityMeta(dialogs.selectedClient.id)
        : { isLoading: false, status: 'idle', unavailable: false, authRequired: false },
    [dialogs.selectedClient, getClientActivityMeta]
  );

  const handleSaveEdit = async (clientPayload) => {
    const result = await updateClient(clientPayload);
    if (!result?.ok) {
      return result;
    }

    dialogs.closeEdit();
    dialogs.openDetail(result.data.client);
    return result;
  };

  const handleSaveNotes = async (clientId, notes) => {
    const result = await saveNotes(clientId, notes);
    if (!result?.ok) {
      return result;
    }

    dialogs.openDetail(result.data.client);
    return result;
  };

  const handleArchiveClient = async (client) => {
    setArchiveRequest(client);
  };

  const confirmArchiveClient = async () => {
    const client = archiveRequest;
    const result = client?.archived ? await restoreClient(client?.id) : await archiveClient(client?.id);
    setArchiveRequest(null);
    if (!result.ok) {
      toast({
        title: client?.archived ? 'Cliente non riattivato' : 'Cliente non disattivato',
        description: result.message || 'Impossibile aggiornare lo stato del cliente.',
        variant: 'destructive',
      });
      return;
    }

    if (client?.archived) {
      dialogs.openDetail(result.data.client);
    } else {
      dialogs.closeDetail();
    }
    toast({
      title: client?.archived ? 'Cliente riattivato' : 'Cliente disattivato',
      description: result.message || 'Stato cliente aggiornato.',
    });
  };

  function openClientScopedRoute(routeId, client) {
    if (!client?.id) return;
    const location = buildRouteLocation(routeId, {
      clientId: client.id,
    });
    if (location) navigate(location);
  }

  return (
    <PageFrame filterModel={moduleFilters} className="clients-page-shell">
      <ClientsHeader />

      {clientsModuleDisabled ? (
        <PageNotice role="alert" className="mb-4 text-sm wt-inline-error">
          {loadError || 'Modulo clienti non abilitato per questo workspace.'}
        </PageNotice>
      ) : null}

      {!clientsModuleDisabled && !clientsTenantDatabaseUnavailable ? (
        <>
          <PartyActivityMetrics activity={activity} />
          <PartyActivityMetricDialog kind="client" activity={activity} onSelect={dialogs.openDetail} />
          <PageDivider />
          <ClientsFilters
            activity={activity}
            filters={filters}
            onSearchChange={setSearch}
            onTipoChange={setTipoCliente}
            onReset={() => {
              setSearch('');
              setTipoCliente('tutti');
              activity.reset();
            }}
            counters={counters}
          />
          <PageDivider />
          <PageActionRow>
            <PrimaryActionButton onClick={dialogs.openCreate}>Nuovo</PrimaryActionButton>
          </PageActionRow>
        </>
      ) : null}

      {loadError && !clientsModuleDisabled ? (
        <PageNotice role="alert" className="mb-4 text-sm wt-inline-error">
          {loadError}
        </PageNotice>
      ) : null}

      {!clientsModuleDisabled && !clientsTenantDatabaseUnavailable ? (
        <ClientsTable
          data={activity.filterRows(moduleFilters.filterRows(filteredClients))}
          onSelect={dialogs.openDetail}
          onEdit={dialogs.openEdit}
          onArchive={handleArchiveClient}
          onDelete={setDeleteRequest}
        />
      ) : null}

      {!clientsModuleDisabled && !clientsTenantDatabaseUnavailable ? (
        <CreateClientDialog
          open={dialogs.createOpen}
          onClose={dialogs.closeCreate}
          onCreate={createClient}
          staff={staff}
          staffUnavailable={staffUnavailable}
          staffError={staffError}
          staffCatalogStatus={staffCatalogStatus}
          emptyClient={emptyClient}
        />
      ) : null}

      {!clientsModuleDisabled && !clientsTenantDatabaseUnavailable ? (
        <EditClientDialog
          open={dialogs.editOpen}
          onClose={dialogs.closeEdit}
          client={dialogs.editingClient}
          onSave={handleSaveEdit}
          staff={staff}
          staffError={staffError}
          staffCatalogStatus={staffCatalogStatus}
        />
      ) : null}

      {!clientsModuleDisabled && !clientsTenantDatabaseUnavailable ? (
        <ClientDetailDrawer
          open={dialogs.detailOpen}
          client={dialogs.selectedClient}
          onClose={dialogs.closeDetail}
          onEdit={dialogs.openEdit}
          onSave={async (draft) => { const result = await handleSaveEdit(draft); if (result?.ok !== false) dialogs.setSelectedClient(result?.data?.client || draft); return result; }}
          onOpenDeals={(client) => openClientScopedRoute(ROUTE_IDS.DEAL_PROPOSALS, client)}
          onOpenAssignments={(client) => openClientScopedRoute(ROUTE_IDS.ASSIGNMENTS, client)}
          onOpenInProgressDeals={(client) => openClientScopedRoute(ROUTE_IDS.DEALS_IN_PROGRESS, client)}
          onOpenCompletedDeals={(client) => openClientScopedRoute(ROUTE_IDS.DEALS_COMPLETED, client)}
          onOpenAccounting={(client) => openClientScopedRoute(ROUTE_IDS.ACCOUNTING, client)}
          onOpenDocuments={(client) => openClientScopedRoute(ROUTE_IDS.DOCUMENTS, client)}
          onSaveNotes={handleSaveNotes}
          onArchive={handleArchiveClient}
          financials={selectedFinancials}
          financialsLoading={selectedFinancialsMeta.isLoading}
          financialsUnavailable={selectedFinancialsMeta.unavailable}
          activity={selectedActivity}
          activityLoading={selectedActivityMeta.isLoading}
          staff={staff}
        />
      ) : null}
      <ConfirmActionDialog
        open={Boolean(archiveRequest)}
        title={archiveRequest?.archived ? 'Riattivare il cliente?' : 'Disattivare il cliente?'}
        description={`Il cliente "${archiveRequest?.name || archiveRequest?.company || 'senza nome'}" verrà ${archiveRequest?.archived ? 'riattivato' : 'disattivato'}.`}
        confirmLabel={archiveRequest?.archived ? 'Riattiva' : 'Disattiva'}
        cancelLabel="Annulla"
        tone={archiveRequest?.archived ? 'default' : 'danger'}
        onCancel={() => setArchiveRequest(null)}
        onConfirm={confirmArchiveClient}
      />
      <ConfirmActionDialog
        open={Boolean(deleteRequest)}
        title="Eliminare definitivamente il cliente?"
        description={`Il cliente "${deleteRequest?.name || deleteRequest?.company || ''}" verrà eliminato in modo irreversibile.`}
        confirmLabel="Elimina definitivamente"
        cancelLabel="Annulla"
        tone="danger"
        onCancel={() => setDeleteRequest(null)}
        onConfirm={async () => {
          const result = await deleteClientPermanently(deleteRequest.id);
          if (result.ok) setDeleteRequest(null);
          else toast({ title: 'Cliente non eliminato', description: result.message || 'Esistono relazioni collegate.', variant: 'destructive' });
        }}
      />
    </PageFrame>
  );
}

export default ClientsPage;
